import React, { memo, useMemo } from 'react';

import { stopEventPropagation } from '@lyearn/core/utils/events';
import { useTranslation } from '@lyearn/i18n';
import { QuizIcon } from '@lyearn/icons';
import { cn } from '@lyearn/style';
import { Box, Stack, Typography } from '@lyearn/ui';
import useNavigation from '@/hooks/useNavigation';
import * as BaseCard from '@/modules/content/components/ContentCard/BaseCard';
import WideCard from '@/modules/content/components/ContentCard/Wide';

import QuizInfo from '../QuizInfo';
import Score from '../Score';
import { PopupQuizCardProps } from './types';
import { getPrimaryActionLabel } from './utils';

const PopupQuizCard = (props: PopupQuizCardProps) => {
  const {
    className,
    content,
    onClick,
    status,
    hideBookmark,
    quiz,
    quizPerformance,
    overrideActionLabel,
    headerAction,
  } = props;
  const { t } = useTranslation();

  const navigation = useNavigation();
  const isAICourseBuilder = navigation.pathname.includes('lyearn-ai/content');

  const primaryActionLabel = useMemo(
    () => overrideActionLabel ?? getPrimaryActionLabel(t, quiz, quizPerformance, isAICourseBuilder),
    [isAICourseBuilder, overrideActionLabel, quiz, quizPerformance, t],
  );

  return (
    <WideCard
      className={cn(className, 'p-20')}
      classes={{ size: '' }}
      content={content}
      direction="col"
      hideBookmark={hideBookmark}
      onClick={onClick}>
      <Stack className="flex-1">
        <QuizIcon className="h-44 w-44 me-12" />
        <Box className="flex-1">
          <BaseCard.Label />
          <BaseCard.Name numberOfLines={2} />
        </Box>
        {headerAction ? (
          <Box className="shrink-0 self-start ms-12" onClick={stopEventPropagation}>
            {headerAction}
          </Box>
        ) : null}
      </Stack>
      <BaseCard.Description className="mt-16" numberOfLines={2} variant="body-long-02" />
      <QuizInfo quiz={quiz} quizPerformance={quizPerformance} />
      {status ? <Score score={quizPerformance?.score || 0} status={status.result} /> : null}
      {primaryActionLabel ? (
        <BaseCard.PrimaryAction className="mt-24 self-start ps-4 text-text-primary">
          <Typography variant="heading-xs"> {primaryActionLabel}</Typography>
        </BaseCard.PrimaryAction>
      ) : null}
    </WideCard>
  );
};

export default memo(PopupQuizCard);
